import { MapContainerProps } from 'react-leaflet';
import { LayerGroup, map, Map, TileLayer } from 'leaflet';
import { useEffect, useRef, useState } from 'react';
import { HidroLayer, HidroLayerGroup } from './layers';
import { Box, Icon, Tooltip, useTheme } from '@mui/material';
import { toPng } from 'html-to-image';
import { SelectTileModal } from './selectTileModal';
import { generateRandomHash } from '../../core';
import { ExpandModal } from './expandModal';
import { googleSatelite } from './tiles';
import "@geoman-io/leaflet-geoman-free";

type ButtonsPosition = "topLeft" | "topRight" | "bottomLeft" | "bottomRight";

interface Props extends MapContainerProps {
  startWithTile?: boolean;
  animateStart?: boolean;
  animateDuration?: number;
  buttonPosition?: ButtonsPosition;
  showTooltips?: boolean;
  recenter?: boolean;
  zoomBtn?: boolean;
  expandable?: boolean;
  screenshot?: boolean;
  chooseMap?: boolean;
  onChooseMap?: (map: TileLayer) => void;
  onCreateMap?: (map: Map) => void;
  limitBounds?: boolean;
  hidroLayers?: HidroLayerGroup;
  externalLayers?: LayerGroup;
  children?: React.ReactNode;
}

export const HidroMap: React.FC<Props> = (data) => {
  const mapRef = useRef<Map | null>(null);

  const mapKey = useRef(generateRandomHash(8));
  const [openLayers, setOpenLayers] = useState(false);
  const [openExpand, setOpenExpand] = useState(false);

  const dataAdd = useRef<Props>({
    zoom: 15,
    center: [-19.9272087, -43.9278269],
    maxBoundsViscosity: 2,
    maxZoom: 18,
    minZoom: 1,
    style: { width: "100%", height: "100%" },
    buttonPosition: data.buttonPosition ?? "topRight",
    zoomControl: false,
    ...data,
  });

  const handlerFinish = (layers: HidroLayerGroup | undefined) => {
    setOpenExpand(false);
    data.hidroLayers?.clearLayers();
    layers?.layers.forEach((layer) => {
      data.hidroLayers?.push(layer);
    });
  };

  const icons: React.ReactNode[] = [];

  if (dataAdd.current.zoomBtn || dataAdd.current.zoomBtn === undefined) {
    icons.push(<ItemIcon
      key={generateRandomHash(3)}
      icon='add'
      onClick={() => mapRef.current?.zoomIn()}
    />);
    icons.push(<ItemIcon
      key={generateRandomHash(3)}
      icon='remove'
      onClick={() => mapRef.current?.zoomOut()}
    />);
  }

  if (dataAdd.current.chooseMap) {
    icons.push(
      <ItemIcon
        key={generateRandomHash(3)}
        icon='layers'
        onClick={() => setOpenLayers(true)}
      />
    );
  }

  if (dataAdd.current.expandable) {
    icons.push(
      <ItemIcon
        key={generateRandomHash(3)}
        icon='aspect_ratio'
        onClick={() => setOpenExpand(true)}
        tooltip={dataAdd.current.showTooltips ? 'Expandir' : undefined}
      />
    );
  }

  if (dataAdd.current.recenter) {
    icons.push(
      <ItemIcon
        key={generateRandomHash(3)}
        icon='center_focus_weak'
        onClick={() => data.hidroLayers?.centerBounds()}
        tooltip={dataAdd.current.showTooltips ? 'Recentralizar' : undefined}
      />
    );
  }

  if (dataAdd.current.screenshot) {
    icons.push(
      <ItemIcon
        key={generateRandomHash(3)}
        icon='photo_camera'
        onClick={() => onScreenshot(mapRef)}
        tooltip={dataAdd.current.showTooltips ? 'Screenshot' : undefined}
      />
    );
  }

  return <div style={{ width: "100%", height: "100%", position: 'relative' }}>

    {/* Botões */}
    {icons.length !== 0 && <Box sx={{
      position: 'absolute',
      display: "flex",
      flexDirection: "column",
      bottom: dataAdd.current.buttonPosition?.startsWith("bottom") ? (dataAdd.current.buttonPosition?.endsWith("Right") ? '28px' : '12px') : undefined,
      left: dataAdd.current.buttonPosition?.endsWith("Left") ? '12px' : undefined,
      top: dataAdd.current.buttonPosition?.startsWith("top") ? '12px' : undefined,
      right: dataAdd.current.buttonPosition?.endsWith("Right") ? '12px' : undefined,
      width: "32px",
      height: `${32 * icons.length}px`,
      zIndex: 1000,
      backgroundColor: "Background",
    }}>
      {icons}
    </Box>}

    {/* Select Modal */}
    {dataAdd.current.chooseMap && openLayers && <SelectTileModal
      key={generateRandomHash(8)}
      open={openLayers}
      onClose={() => setOpenLayers(false)}
      onAdd={() => setOpenLayers(false)}
      layerGroup={data.hidroLayers} />}

    {/* Expand modal */}
    {data.expandable && openExpand && <ExpandModal
      key={generateRandomHash(8)}
      open={openExpand}
      layergroup={data.hidroLayers?.copy()}
      onFinish={handlerFinish}
    ><></></ExpandModal>}

    {/* Mapa */}
    <Mapa
      key={mapKey.current}
      mapRef={mapRef}
      {...dataAdd.current}
      hidroLayers={data.hidroLayers}
      onCreateMap={data.onCreateMap}
    />
  </div>;
};

interface PropsMapa extends Props {
  limitBounds?: boolean;
  hidroLayers?: HidroLayerGroup;
  externalLayers?: LayerGroup;
  mapRef: React.MutableRefObject<Map | null>;
  children?: React.ReactNode;
  onCreateMap?: (map: Map) => void;
}

export const Mapa: React.FC<PropsMapa> = (data) => {
  const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const mapRef = data.mapRef;
  const mapContainerRef = useRef(null);
  const hidroLayers = data.hidroLayers;
  const startRef = useRef(false);

  const [mapId, setMapId] = useState(generateRandomHash(8));

  // Listener do Mapa
  const listener = (group: HidroLayerGroup) => {
    timerRef.current = undefined;

    if (!mapRef.current) return;

    // Layers iniciais
    if (data.startWithTile && group.layers.length === 0) {
      const layer = googleSatelite();
      group.push(
        new HidroLayer({
          name: 'Mapa',
          layer: layer,
          showing: true,
        })
      );
    }

    mapRef.current.eachLayer((layer) => mapRef.current?.removeLayer(layer));

    // Layers Externas vem primeiro
    if (data.externalLayers) mapRef.current.addLayer(data.externalLayers);
    const layers = group.getLayers();
    mapRef.current.addLayer(layers);

    const bounds = group.getBounds();
    if (!bounds) return;

    if (!startRef.current) {
      setTimeout(() => {
        startRef.current = true;
      }, 500);
      group.centerBounds({
        animate: data.animateStart ?? false,
        duration: data.animateDuration ?? 5,
      });
    }

    if (data.limitBounds) mapRef.current.setMaxBounds(bounds);
    setMapId(generateRandomHash(8));
  };

  //Mapa
  useEffect(() => {
    if (mapRef.current || !mapContainerRef.current) return;

    const mapa = map(mapContainerRef.current, {
      ...data,
      zoom: 15,
      center: [-19.9272087, -43.9278269],
    });
    mapRef.current = mapa;

    setMapId(generateRandomHash(8));

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [mapContainerRef.current]);

  // Adicionando os listeners e mapa
  useEffect(() => {
    if (!mapRef.current) return;

    hidroLayers?.setMap(mapRef.current);
    if (hidroLayers) listener(hidroLayers);

    const listenerId = hidroLayers?.addListener(listener) ?? "";

    setMapId(generateRandomHash(8));

    return () => {
      hidroLayers?.removeListener(listenerId);
      hidroLayers?.setMap(undefined);
    };

  }, [mapRef.current]);

  useEffect(() => {
    if (!mapRef.current) return;
    if (data.onCreateMap) data.onCreateMap(mapRef.current);
  }, [mapId]);

  return <div
    id={mapId}
    ref={mapContainerRef}
    style={{ height: '100%', width: '100%' }}
  />;
};



const onScreenshot = (mapRef: React.MutableRefObject<Map | null>) => {
  if (mapRef.current === null) return;

  toPng(mapRef.current.getContainer(), { cacheBust: true })
    .then((dataUrl) => {
      const link = document.createElement('a');
      link.download = 'map.png';
      link.href = dataUrl;
      link.click();
    })
    .catch((err) => {
      console.log('Erro ao tirar screenshot', err);
    });
};

interface PropsIcon {
  onClick: () => void;
  icon: string;
  tooltip?: string;
}
const ItemIcon: React.FC<PropsIcon> = ({ icon, onClick, tooltip }) => {
  const theme = useTheme();

  const component = <Box
    onClick={onClick}
    sx={{
      width: "32px",
      height: "32px",
      display: "flex",
      borderRadius: "2px",
      alignItems: "center",
      justifyContent: "center",
      border: `2px solid ${theme.palette.primary.main}`,
      backgroundColor: theme.palette.background.paper,
      ":hover": {
        backgroundColor: "rgba(0,0,0,0.2)",
      },
    }}>
    <Icon fontSize="small" sx={{ color: theme.palette.primary.main }}>{icon}</Icon>
  </Box>;

  if (!tooltip) {
    return component;
  }

  return <Tooltip
    arrow
    title={tooltip}>
    {component}
  </Tooltip>;
};
